import { SlashIDEnvironment, UserTokenText } from "."
import { versionInfo as version } from "../version"
import * as api from "../clients/slashid-api"
import * as time from "../utils/time"
import * as domain from "../domain/index"
import * as errors_INTERNAL_ONLY from "./errors.internal"
import * as base64 from "../utils/base64"
import { SlashIDConfig, createSlashIDConfig } from "./options"
import { createRetryOnNetworkErrorMiddleware } from "../utils/fetch"
import { Attestation, Challenge, ChallengeSSR, isNonceChallenge, isProxyChallenge } from "./challenges.types"
import { isValidHttpUrl } from "../utils/url"
import { ERROR_NAMES } from "./errors"

export type ChallengeProcessorOptions = {
    /** HTTP client that implements the Fetch API */
    fetchApi?: api.Configuration["fetchApi"]
    /** The environment to use for the SlashID API. */
    environment?: SlashIDEnvironment
    /**
     * The origin of the request.
     * Used to identify the origin of the request when sending requests to the SlashID API.
     * If allowed domains are configured in the SlashID API, the origin must be one of the allowed domains.
     * Otherwise, use the default value as is.
     */
    origin?: string
}

export type Config = Pick<SlashIDConfig, "baseURL">

/**
 * A server-side challenge processor. Compatible with node versions >= 18.
 * Node runtimes < 18 require a Fetch API implementation to be provided.
 *
 * Parse query string containing challenges and resolve them server side.
 */
export class ChallengeProcessor {
    private authnClient: api.DefaultApi
    private config: SlashIDConfig
    private fetchApi: api.Configuration["fetchApi"]
    private requestOrigin: URL

    constructor({ origin = "http://localhost", environment, fetchApi }: ChallengeProcessorOptions = {}) {
        // create the API client
        // make sure it is compatible - min node version or isomorphic-fetch
        this.config = createSlashIDConfig({ environment })
        this.fetchApi = fetchApi || fetch

        if (!isValidHttpUrl(origin)) {
            throw new errors_INTERNAL_ONLY.SlashIDError({
                name: ERROR_NAMES.invalidActionError,
                message: "Invalid origin URL",
                context: { origin },
            })
        }

        this.requestOrigin = new URL(origin)

        this.authnClient = new api.DefaultApi(
            new api.Configuration({
                ...api.DefaultConfig,
                basePath: this.config.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
                fetchApi: this.fetchApi,
            })
        )
    }

    private updateClient(token?: UserTokenText) {
        this.authnClient = new api.DefaultApi(
            new api.Configuration({
                ...api.DefaultConfig,
                basePath: this.config.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
                fetchApi: this.fetchApi,
                ...(token && { accessToken: token }),
            })
        )
    }

    /**
     * Input must be a query string containig either 'challenges' or 'sidcp' URL query parameters.
     * Get a Promise that resolves to an array of challenge objects.
     * Theses objects are either nonce or proxy challenges.
     */
    public async parseChallenges(queryString: string): Promise<ChallengeSSR[] | null> {
        const urlParams = new URLSearchParams(queryString)

        let challengeObjs: api.ChallengeListInner[] | undefined = []

        const challengePackId = urlParams.get(domain.ChallengePackURLOption)
        const encodedChallenges = urlParams.get(domain.ChallengesURLOption)

        if (challengePackId && encodedChallenges) {
            throw new errors_INTERNAL_ONLY.SlashIDError({
                name: ERROR_NAMES.invalidSlashIDURLParamsError,
                message: "Both 'challenges' and 'sidcp' URL query parameters are provided.",
                context: {
                    queryString,
                },
            })
        }

        if (challengePackId) {
            // we got a challenge pack ID => get the challenges
            const response = await this.authnClient.getChallengePackChallengePackId(
                { challengePackId, slashIDSdkVersion: version.raw },
                {
                    cache: "no-cache",
                }
            )
            if (response.errors) {
                throw new errors_INTERNAL_ONLY.SlashIDError({
                    name: ERROR_NAMES.apiResponseError,
                    message: "API response error",
                    context: {
                        queryString,
                        responseDetails: response,
                    },
                })
            }
            challengeObjs = response.result
        } else if (encodedChallenges) {
            // check whether challenges are encoded directly in the params
            const challengesData = base64.decode(encodedChallenges)
            const challengesStr = new TextDecoder("utf-8").decode(challengesData)
            challengeObjs = JSON.parse(challengesStr)
        }

        // must return null if no challenges are found
        if (!challengeObjs || challengeObjs.length === 0) {
            return null
        }
        return challengeObjs.filter(
            (challenge) => isNonceChallenge(challenge) || isProxyChallenge(challenge)
        ) as ChallengeSSR[]
    }

    /**
     * Process an array of challenges. The challenges must be either nonce or proxy challenges.
     * Returns a Promise that resolves to a user token if the challenge resolution is successful.
     */
    public async processChallenges({
        challenges,
        previousToken,
    }: {
        challenges: ChallengeSSR[]
        previousToken?: UserTokenText
    }): Promise<domain.UserTokenText> {
        this.updateClient(previousToken)

        let userToken: domain.UserTokenText | undefined = undefined

        try {
            for (const ch of challenges) {
                userToken = await this.process(ch)
            }

            if (!userToken) {
                throw new Error("no user token in attestation response")
            }

            return userToken
        } catch (e: any) {
            // ensure that the top level error is a SlashIDError
            throw await errors_INTERNAL_ONLY.toSlashIDError(e)
        }
    }

    private async process(challenge: Challenge): Promise<domain.UserTokenText> {
        let attestation: Attestation

        switch (challenge.type) {
            case api.ChallengeType.Proxy:
                return this.processProxyChallenge(challenge as api.ProxyChallenge)
            case api.ChallengeType.Nonce:
                attestation = await this.processNonceChallenge(challenge as api.NonceChallenge)
                break

            default:
                throw new Error("unhandled challenge type: " + JSON.stringify(challenge))
        }

        let response
        try {
            response = await this.authnClient.postAttestationV3(
                {
                    // @ts-expect-error challenge_type is typed as possible undefined
                    attestation: attestation,
                    slashIDSdkVersion: version.raw,
                },
                {
                    cache: "no-cache",
                    headers: {
                        origin: this.requestOrigin.toString(),
                        accept: "application/json",
                        "content-type": "application/json",
                    },
                }
            )
        } catch (e: any) {
            if (errors_INTERNAL_ONLY.isResponseError(e)) {
                response = await e.response.clone().json()
            } else {
                throw e
            }
        }

        if (errors_INTERNAL_ONLY.isThrownResponse(response) && response.errors.length) {
            throw response
        }

        const userToken = response.result.token
        if (!userToken) {
            throw new Error("no user token in attestation response")
        }

        return userToken
    }

    private async processProxyChallenge(challenge: api.ProxyChallenge): Promise<UserTokenText> {
        let userToken: UserTokenText | undefined = undefined
        let count = 0
        const threshold = 30

        while (!userToken) {
            if (count++ > threshold) {
                throw new errors_INTERNAL_ONLY.SlashIDError({
                    name: ERROR_NAMES.timeoutError,
                    message: "Timed out waiting for user token",
                    context: { challenge },
                })
            }

            await time.sleep(1000)

            // TODO review
            const response = await this.authnClient.getChallengeChallengeIdV2(
                {
                    challengeId: challenge.options!!.challenge_id!!,
                    slashIDSdkVersion: version.raw,
                },
                { cache: "no-cache" }
            )
            // v7 client returns null on HTTP 202 (still pending) — loop continues.
            if (response?.errors) {
                if (response.errors.length !== 1 || response.errors[0].httpcode !== 202) {
                    throw response
                }
            }

            userToken = response?.result
        }

        return userToken
    }

    private async processNonceChallenge(challenge: api.NonceChallenge): Promise<api.NonceAttestation> {
        const attestation: api.NonceAttestation = {
            challenge_id: challenge.id,
            challenge_type: challenge.type,
            data: {
                challenge: challenge.options?.challenge,
            },
        }

        return attestation
    }
}
